import { css } from '@emotion/react';
import React from 'react';

import { useTranslations } from '~src/commons/utils/translations/translate';
import {
  formatMainDeliveryTimePart,
  formatSecondaryDeliveryTimePart,
  getTimeSpanFromDeliveryTime,
} from '~src/delivery-time/formatters';
import { useGlobalState } from '~src/global-state';
import { fontStyles } from '~src/theme/fontStyles';
import { mediaQueries } from '~src/theme/mediaQueries';

import { DeliveryTime } from '../../api/api';
import { colors } from '../theme/colors';
import { ThemeModel } from '../theme/theme';
import { AriaGrid, useAriaGridCell } from '../ui/aria-grid';
import { ListState } from '../ui/list-state';
import { useScrollIntoView } from '../ui/scroll-into-view';

interface TimeSlotListProps {
  listState: ListState<DeliveryTime>;
}

interface TimeSlotListItemProps {
  listState: ListState<DeliveryTime>;
  deliveryTime: DeliveryTime;
}

const ListItem: React.FC<TimeSlotListItemProps> = ({ listState, deliveryTime }) => {
  const { tICU, formatDate, locale } = useTranslations();
  const isTomorrowHoliday = !!useGlobalState().additional?.is_tomorrow_holiday;

  const ref = React.useRef(null);

  const { cellProps, isSelected } = useAriaGridCell({
    itemKey: deliveryTime.id,
    listState,
    ref,
    enableAutoSelect: true,
  });

  useScrollIntoView(ref, isSelected);

  const shouldRenderSecondaryDeliveryTime = deliveryTime.format === 'weekday_date';

  return (
    <div
      ref={ref}
      className="focus-visible-outline"
      css={styles.itemContainer(isSelected)}
      {...cellProps}
    >
      <div css={styles.upperContainer}>
        <span css={styles.textStyle}>
          {formatMainDeliveryTimePart({
            deliveryTime,
            translate: tICU,
            dateFormatter: formatDate,
            locale,
            isTomorrowHoliday,
          })}
        </span>
        <span css={styles.rightText}>{getTimeSpanFromDeliveryTime(deliveryTime, tICU)}</span>
      </div>
      {shouldRenderSecondaryDeliveryTime && (
        <div>
          <span css={styles.secondaryText}>
            {formatSecondaryDeliveryTimePart(deliveryTime.max, formatDate)}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * TODO: User aria grid only for timeslot selection now for test. Later move it
 * to other parts of app.
 */
export const TimeSlotList: React.FC<TimeSlotListProps> = ({ listState }) => {
  return (
    <AriaGrid css={styles.list} listState={listState}>
      {(deliveryTime: DeliveryTime) => (
        <ListItem key={deliveryTime.id} deliveryTime={deliveryTime} listState={listState} />
      )}
    </AriaGrid>
  );
};

const textStyle = {
  ...fontStyles.px12_18,
  fontVariationSettings: '"wght" 500',
};

const styles = {
  textStyle: css(textStyle),
  rightText: css(textStyle),
  secondaryText: css({ ...textStyle, color: colors.neutral35 }),
  list: {
    maxHeight: '100%',
    display: 'grid',
    gridTemplateColumns: 'auto',
    columnGap: '16px',
    rowGap: '16px',
    margin: '0 40px',
    [mediaQueries.S]: {
      margin: '0 16px',
    },
  },
  itemContainer: (active: boolean) => (theme: ThemeModel) =>
    css({
      height: '70px',
      padding: `${active ? 15 : 16}px`,
      borderWidth: active ? '2px' : '1px',
      borderStyle: 'solid',
      borderColor: active ? theme.accent48 : colors.neutral85,
      ':hover': {
        borderColor: active ? theme.accent48 : colors.neutral75,
      },
      cursor: active ? 'initial' : 'pointer',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }),
  upperContainer: css({
    display: 'flex',
    justifyContent: 'space-between',
  }),
};
